<script setup lang="ts">
/**
 * 聊天主界面。
 *
 * **最危险的一行是 `onConversationCreated` 里的 `window.history.replaceState`。**
 * 原实现（Next）的注释记录了一次实测事故：用路由 API 把地址从 `/chat` 换成
 * `/chat/<id>` 时，dev 环境下退化成了整页导航——文档重载、输入框被换掉、
 * 正在进行的流式回答一起消失，用户的第一个问题就这么没了。
 * `history.replaceState` 只改地址栏，不碰任何运行时状态。
 *
 * 代价：路由库内部的"当前路由"仍停在 `/chat`，所以"开新会话"不能再依赖
 * "导航到 /chat"（会被判定为空操作），必须显式 `chat.reset()`。
 * 在 vue-router 里多一层保障：`/chat/:id?` 是**同一条路由**（可选参数），
 * 参数变化不会重挂组件——所以流式过程中的地址替换完全不会打断接收。
 * 仍然用 `replaceState`：进入历史会话时不产生额外的历史记录项，
 * 浏览器"后退"回到的是上一个页面，而不是上一个会话。
 */

import { computed, onBeforeUnmount, onMounted, ref, toRef } from 'vue';
import { useRouter } from 'vue-router';
import { DOMAIN_META, type AgeBand, type Domain } from '@spirit/contracts';

import AgeBandPicker from './AgeBandPicker.vue';
import ChatInput from './ChatInput.vue';
import MessageList from './MessageList.vue';
import Button from '../ui/Button.vue';
import { useAuth } from '../../lib/auth-store';
import { registerChatReset } from '../../lib/chat-reset-registry';
import { useChatController } from '../../lib/chat-store';
import { isMockEnabled } from '../../lib/env';
import { rateLimit } from '../../lib/rate-limit';

const props = defineProps<{ conversationId?: string | undefined; initialDomain?: Domain | undefined }>();

const router = useRouter();
const auth = useAuth();
const ageBand = ref<AgeBand | null>(null);

/** 只改地址栏，不动运行时状态——见文件头注释。 */
function onConversationCreated(id: string): void {
  window.history.replaceState(null, '', `/chat/${id}`);
}

const chat = useChatController({
  conversationId: toRef(props, 'conversationId'),
  domain: toRef(props, 'initialDomain'),
  ageBand,
  onConversationCreated,
});

let unregisterReset: (() => void) | null = null;

function handleNewConversation(): void {
  // 顺序不能反：先本地重置，再改地址。反了会看到"地址已是 /chat、内容还是旧会话"的一帧。
  chat.reset();
  void router.replace('/chat');
}

onMounted(() => {
  // 把控制器交给导航栏的「对话」标签：从 /chat/<id> 点「对话」时要能开新会话，
  // 而不是因为"路由没变"什么都不发生。
  unregisterReset = registerChatReset(() => chat.reset());
});

onBeforeUnmount(() => {
  unregisterReset?.();
  unregisterReset = null;
});

const needsLogin = computed(() => auth.hydrated && !auth.isAuthenticated);
const domainMeta = computed(() =>
  props.initialDomain === undefined ? null : DOMAIN_META[props.initialDomain],
);
const rateLimitActive = computed(() => rateLimit.snapshot.active);
const rateLimitSeconds = computed(() => rateLimit.snapshot.secondsRemaining);

/** 输入框的禁用原因：只有限流才给理由；未登录时整块换成登录引导。 */
const disabledReason = computed(() =>
  rateLimitActive.value ? `请求过于频繁，请在 ${rateLimitSeconds.value} 秒后重试` : null,
);
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="border-b border-slate-200 bg-white px-4 py-2.5">
      <div class="mx-auto flex max-w-3xl items-center justify-between gap-3">
        <div class="min-w-0">
          <h1 class="truncate text-sm font-semibold text-slate-900">
            {{ domainMeta === null ? 'AI 智能咨询' : `${domainMeta.label}咨询` }}
          </h1>
          <p class="truncate text-xs text-slate-500">
            {{ chat.conversationId === null ? '新会话' : `会话 ${chat.conversationId}` }}
            <template v-if="domainMeta !== null"> · {{ domainMeta.safetyNote }}</template>
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          @click="handleNewConversation"
        >
          新对话
        </button>
      </div>

      <div v-if="props.initialDomain === 'family_education'" class="mx-auto mt-2 max-w-3xl">
        <AgeBandPicker
          :value="ageBand === null ? [] : [ageBand]"
          :max="1"
          label="本次针对的年龄段（可选）"
          hint="不选则由服务端按提问内容推断"
          id-prefix="chat-age-band"
          @change="(next) => (ageBand = next[0] ?? null)"
        />
      </div>
    </div>

    <MessageList
      :items="chat.items"
      :loading="chat.loadingHistory"
      :history-error="chat.historyError"
      :reset-key="chat.conversationId ?? 'new'"
      :on-regenerate="() => void chat.regenerate()"
      :on-suggestion="(text) => void chat.send(text)"
    />

    <div v-if="needsLogin" class="border-t border-slate-200 bg-white px-4 py-4">
      <div class="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
        <p class="text-sm text-slate-600">
          登录后即可开始咨询，额度与历史会话会保留在你的账号下。
          <span v-if="isMockEnabled()" class="text-slate-400">
            （当前为 Mock 模式，任意手机号 + 6 位验证码即可登录）
          </span>
        </p>
        <RouterLink to="/login"><Button size="md">去登录</Button></RouterLink>
      </div>
    </div>

    <ChatInput
      v-else
      :sending="chat.sending"
      :on-send="(text) => chat.send(text)"
      :on-stop="chat.stop"
      :disabled="rateLimitActive"
      :disabled-reason="disabledReason"
      auto-focus
    />
  </div>
</template>
